import React from 'react'
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SelectClub from '@/components/features/clubs/select-club';
import SelectPlayer from '@/components/features/players/select-player';
import SelectPosition from '@/components/features/players/select-position';
import SelectRolePosition from '@/components/features/players/select-role-position';
import { Textarea } from '@/components/ui/textarea';
import MultiSelect from '@/components/ui/multi-select';
import { LANGUAGES } from '@/constants/languages';
import { Input } from '@/components/ui/input';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { useTRPC } from '@/trpc';
import { TRPCClientError } from '@trpc/client';
import { TContactPlayerUpdate } from '@/types/trpc.types';
import * as Icon from 'lucide-react'

interface Props {
    player_offered_id: string;
    defaultValues?: Partial<UpdatePlayersOfferedSchema>
    trigger?: React.ReactNode;
    onUpdate?: (player:TContactPlayerUpdate) => void;
}

const updatePlayersOfferedSchema = z.object({
    player_offered_id: z.string().uuid(),
    offered_by_id: z.string().uuid().optional(),
    mongo_player_id: z.string().min(1),
    mongo_club_id: z.string().optional(),
    external_url: z.string().optional(),
    position: z.string().optional(),
    role_position: z.string().optional(),
    note: z.string().optional(),
    languages: z.array(z.string()).optional(),
    mandate: z.string().optional(),
    telephone: z.string().optional(),
    email: z.string().email().optional(),
    transfer_amount: z.number().optional(),
    salary_expectation: z.number().optional(),
    extra_info: z.string().optional(),
    company: z.string().optional(),
})
type UpdatePlayersOfferedSchema = z.infer<typeof updatePlayersOfferedSchema>

const UpdatePlayersOffered = ({ player_offered_id, defaultValues, trigger, onUpdate }: Props) => {
    const { trpc } = useTRPC();
    const [open, setOpen] = React.useState(false);

    const form = useForm<UpdatePlayersOfferedSchema>({
        defaultValues: {
            player_offered_id: player_offered_id,
            ...defaultValues
        }
    })
    const club_id = form.watch("mongo_club_id")

    const updateMutation = useMutation({
        mutationFn: async (data:UpdatePlayersOfferedSchema) => {
            const result = await trpc.contacts.playersOffered.update.mutate(data);
            return result as TContactPlayerUpdate
        },
        onSuccess: (result) => {
            toast.success("Speler succesvol gewijzigd.")
            setOpen(false);
            onUpdate?.(result)
            form.reset();
        },
        onError: (err) => {
            if(err instanceof Error) {
                toast.error(err.message);
            } else if(err instanceof TRPCClientError) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden.");
            }
        }
    })

    const handleInputFileChange = (e:React.ChangeEvent<HTMLInputElement>, key:keyof UpdatePlayersOfferedSchema) => {
        const file = e.target.files?.[0];
        if(file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                form.setValue(key, reader.result as string);
            };
        }
    }

    const handleSubmit = () => {
        const values = form.getValues();
        if(!updatePlayersOfferedSchema.safeParse(values).success) {
            toast.error("Vul alle velden correct in.");
            return;
        }
        updateMutation.mutate(values);
    }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent className='gap-0'>
            <SheetHeader>
                <SheetTitle>Aangeboden speler wijzigen</SheetTitle>
                <SheetDescription>Vul de gewenste gegevens in</SheetDescription>
            </SheetHeader>
            <div className='h-full overflow-auto p-4'>
                <div className='flex flex-col gap-4'>
                    <div>
                        <Label>Club</Label>
                        <Controller 
                            control={form.control}
                            name="mongo_club_id"
                            render={({field})=>(
                                <SelectClub 
                                    value={field.value} 
                                    onValueChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label required>Speler</Label>
                        <Controller 
                            control={form.control}
                            name="mongo_player_id"
                            render={({field})=>(
                                <SelectPlayer 
                                    value={field.value} 
                                    onValueChange={field.onChange}
                                    searchOptions={{
                                        competition: {
                                            club_id: club_id
                                        }
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className='flex items-center gap-4'>
                        <div>
                            <Label>Positie</Label>
                            <Controller 
                                control={form.control}
                                name="position"
                                render={({field})=>(
                                    <SelectPosition 
                                        selectedValue={field.value} 
                                        onValueChange={field.onChange} 
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <Label>Rol in positie</Label>
                            <Controller 
                                control={form.control}
                                name="role_position"
                                render={({field})=>(
                                    <SelectRolePosition 
                                        selectedValue={field.value} 
                                        onValueChange={field.onChange} 
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Externe url</Label>
                        <Controller
                            control={form.control}
                            name="external_url"
                            render={({field})=>(
                                <Input 
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Notitie</Label>
                        <Controller
                            control={form.control}
                            name="note"
                            render={({field})=>(
                                <Textarea 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Talen</Label>
                        <Controller
                            control={form.control}
                            name="languages"
                            render={({field})=>(
                                <MultiSelect 
                                    selectedOptions={field.value ?? []} 
                                    onValueChange={field.onChange} 
                                    options={LANGUAGES}
                                    placeholder='Selecteer talen'
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Mandaat</Label>
                        <Controller
                            control={form.control}
                            name="mandate"
                            render={({field})=>(
                                <Input 
                                    type="file"
                                    onChange={(e)=>{handleInputFileChange(e, 'mandate')}} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Voor bedrijf</Label>
                        <Controller
                            control={form.control}
                            name="company"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Contact telefoon</Label>
                        <Controller
                            control={form.control}
                            name="telephone"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Contact e-mail</Label>
                        <Controller
                            control={form.control}
                            name="email"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Transferbedrag</Label>
                        <Controller
                            control={form.control}
                            name="transfer_amount"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={(e)=>field.onChange(e.target.valueAsNumber)} 
                                    type="number"
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Loonsverwachting</Label>
                        <Controller
                            control={form.control}
                            name="salary_expectation"
                            render={({field})=>(
                                <Input 
                                    value={field.value} 
                                    onChange={(e)=>field.onChange(e.target.valueAsNumber)} 
                                    type="number"
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Label>Extra info</Label>
                        <Controller
                            control={form.control}
                            name="extra_info"
                            render={({field})=>(
                                <Textarea 
                                    value={field.value} 
                                    onChange={field.onChange} 
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            <SheetFooter>
                <div className='flex items-center gap-2 justify-end'>
                    <Button onClick={()=>{setOpen(false)}} variant='outline'>Annuleren</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!form.formState.isValid || updateMutation.isLoading}
                    >
                        {updateMutation.isLoading && <Icon.Loader2 className="w-4 h-4 animate-spin" />}
                        Opslaan
                    </Button>
                </div>
            </SheetFooter>
        </SheetContent>
    </Sheet>
  )
}

export default UpdatePlayersOffered